import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import uniqueId from 'lodash/uniqueId';

import { useEditor } from '@splashthat/splash-cms-engine';
import { SuiContainer, SuiNotification } from '@splashthat/sui';

import { getLibrarianApiClient } from 'lib/SplashApiClient/subclients/LibrarianApiClient';
import { useNotificationManager } from 'common/compounds/NotificationManager';
import { getUserConfirmation } from 'common/components/AppNavigation/utils';

import { useEvent } from 'common/store/events';
import { useUser } from 'common/store/user';

import { serializeEditorData } from 'common/components/TouchpointEditor/utils/serializeEditorData';
import Workspace from 'common/components/Workspace';
import ArrowLink from 'common/components/ArrowLink';
import { UndoRedoButtons } from 'common/components/TouchpointEditor/components/UndoRedoButtons';
import { ButtonsContainer } from './Topbar.styles';
import Spacer from '../../../components/Spacer';
import { containerPaddingM } from '../../../styles/variables';

const SUBTITLE = 'Editing';
const TITLE = 'Confirmations';
const UNSAVED_CHANGES_TEXT = 'You have unsaved changes. Do you wish to continue?';

const ERROR_MESSAGE_SAVE = 'Error saving confirmation settings. Please try again.';

export default function TopBar({ goToConfirmationsSettings, confirmationsState }) {
    const editor = useEditor((state) => ({
        hasUnsavedChanges: state.hasUnsavedChanges,
    }));
    const event = useEvent();
    const user = useUser();
    const notificationManager = useNotificationManager();
    const confirmationsApiClient = useRef(getLibrarianApiClient(event.id)).current;

    const {
        mutate: save,
        isLoading,
        isError,
    } = useMutation(
        () =>
            confirmationsApiClient.saveEventPageConfirmation({
                state: confirmationsState,
                userId: user.id,
                document: editor.actions.serialize(serializeEditorData),
            }),
        {
            onSuccess: editor.actions.resetUnsavedChanges,
            onError: () =>
                notificationManager.showNotification({
                    id: uniqueId('CONFIRMATIONS_SAVE_'),
                    message: ERROR_MESSAGE_SAVE,
                    variation: SuiNotification.ERROR,
                }),
        },
    );

    const leaveConfirmationsTouchpoint = () => {
        if (editor.hasUnsavedChanges) {
            getUserConfirmation(UNSAVED_CHANGES_TEXT, (confirmContinue) => {
                if (confirmContinue) {
                    goToConfirmationsSettings();
                }
            });
        } else {
            goToConfirmationsSettings();
        }
    };

    useEffect(() => {
        const beforeUnloadListener = (e) => {
            e.preventDefault();
            return (e.returnValue = '');
        };

        if (editor.hasUnsavedChanges) {
            window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });
        } else {
            window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
        }

        return () => window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
    }, [editor.hasUnsavedChanges]);

    return (
        <SuiContainer variation="flex-horizontal full-width full-height">
            <ArrowLink onClick={leaveConfirmationsTouchpoint} href="#" />
            <Workspace.Topbar.Title subtitle={SUBTITLE} title={TITLE} />
            <ButtonsContainer>
                <UndoRedoButtons />
            </ButtonsContainer>
            <Spacer $spacingY={containerPaddingM} />
            <Workspace.Topbar.SaveButton isSaving={isLoading} onClick={save} hasSaveError={isError} />
        </SuiContainer>
    );
}

TopBar.propTypes = {
    goToConfirmationsSettings: PropTypes.func.isRequired,
    confirmationsState: PropTypes.string.isRequired,
};
